import { useRef, useState, useEffect, useCallback } from "react";
import { gamepadControl, DISCRETE_ACTIONS } from "@/lib/manualMapping";
import { KEY_DIR, KEY_DISCRETE, normalizeIntent, hasContinuous, arbitrate } from "@/lib/pilotInput";

// COCKPIT.FINAL.7 — abstraction d'entrée pilote COMMUNE. Keyboard / Touch / Gamepad passent ici et sont
// routés vers useRealOffboardControl (AUTORITAIRE) via ses méthodes EXISTANTES : press/release (axes
// directionnels), setAnalog (gamepad), hover/landReal/rtlReal/speedUp/speedDown. Aucune logique Flight
// Control, aucune limite de vitesse ici. Un seul owner d'axes continus à la fois (arbitre pur). deadman
// fail-closed. Perte anormale de source -> les sécurités EXISTANTES de useRealOffboardControl (safetyZero
// sur blur/visibility/pagehide/pointercancel/gamepaddisconnected) restent seules responsables.
export function usePilotInput({ real, padRef, profile, enabled = true } = {}) {
  const ownerRef = useRef(null);          // source possédant les axes continus
  const keyHeldRef = useRef(new Set());   // directions clavier maintenues
  const touchHeldRef = useRef(new Set()); // directions tactiles maintenues
  const prevBtnRef = useRef([]);
  const lastInputAtRef = useRef(null);
  const padResetRef = useRef(false);      // PILOT.BLOCKERS.1 : neutre manette à notifier après perte de source
  // R1/R2 — boutons discrets : au montage, au chargement du profil et après perte de focus/onglet/source,
  // chaque bouton doit être vu RELÂCHÉ (page visible ET fenêtre focalisée) avant qu'un NOUVEL appui puisse
  // déclencher hover/LAND/RTL. Aucune action discrète hors focus réel.
  const btnGuardRef = useRef({ armed: new Set() });
  const profileKey = profile ? (profile.id ?? JSON.stringify(profile.button_mapping || {})) : null;
  useEffect(() => { btnGuardRef.current = { armed: new Set() }; }, [profileKey]);
  const [obs, setObs] = useState({ activeSource: null, deadman: false, active: false, lastInputAt: null });

  const markObs = useCallback((source, active) => {
    if (active) lastInputAtRef.current = Date.now();
    setObs({ activeSource: active ? source : null, deadman: active, active, lastInputAt: lastInputAtRef.current });
  }, []);

  // ---- Clavier : axes directionnels (WASD/flèches/RFQE) + discrets hover/land/rtl (décollage exclu). ----
  useEffect(() => {
    if (!enabled || !real) return undefined;
    const onDown = (e) => {
      const d = KEY_DISCRETE[e.code];
      if (d) { e.preventDefault(); if (d === "hover") real.hover(); else if (d === "land") real.landReal(); else if (d === "rtl") real.rtlReal(); return; }
      const dir = KEY_DIR[e.code];
      if (!dir || e.repeat) return;
      e.preventDefault();
      if (arbitrate(ownerRef.current, "keyboard", true) !== "keyboard") return; // owner occupé
      ownerRef.current = "keyboard";
      keyHeldRef.current.add(dir);
      real.press(dir);
      markObs("keyboard", true);
    };
    const onUp = (e) => {
      const dir = KEY_DIR[e.code];
      if (!dir) return;
      if (!keyHeldRef.current.has(dir)) return;
      keyHeldRef.current.delete(dir);
      real.release(dir);
      if (keyHeldRef.current.size === 0) { ownerRef.current = arbitrate(ownerRef.current, "keyboard", false); markObs(null, false); }
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => { window.removeEventListener("keydown", onDown); window.removeEventListener("keyup", onUp); };
  }, [enabled, real, markObs]);

  // PILOT.BLOCKERS.1 — perte de focus/onglet/pointeur/manette : relâchements possiblement perdus ->
  // touches, contacts et propriétaire effacés (le zéro autoritaire reste safetyZero). Nouvelle action requise.
  useEffect(() => {
    if (!enabled || !real) return undefined;
    const reset = () => {
      keyHeldRef.current.clear(); touchHeldRef.current.clear();
      ownerRef.current = null; padResetRef.current = true;
      btnGuardRef.current = { armed: new Set() };
      markObs(null, false);
    };
    const onVis = () => { if (document.visibilityState === "hidden") reset(); };
    const evs = ["blur", "pagehide", "pointercancel", "gamepaddisconnected"];
    evs.forEach((e) => window.addEventListener(e, reset));
    document.addEventListener("visibilitychange", onVis);
    return () => {
      evs.forEach((e) => window.removeEventListener(e, reset));
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [enabled, real, markObs]);

  // ---- Gamepad : axes analogiques (setAnalog) + boutons discrets. Cadence conservée (~50 ms). ----
  useEffect(() => {
    if (!enabled || !real) return undefined;
    const id = setInterval(() => {
      const snap = padRef?.current;
      const g = gamepadControl(snap, profile);
      const intent = normalizeIntent({ pitch: g.pitch, roll: g.roll, lift: g.lift, yaw: g.yaw }, "gamepad");
      const active = hasContinuous(intent);
      const owner = arbitrate(ownerRef.current, "gamepad", active);
      if (!active && padResetRef.current) {   // manette revenue au neutre après perte de source
        padResetRef.current = false;
        real.setAnalog({ pitch: 0, roll: 0, lift: 0, yaw: 0 });
      }
      if (active && owner === "gamepad") {
        ownerRef.current = "gamepad";
        real.setAnalog({ pitch: intent.pitch, roll: intent.roll, lift: intent.lift, yaw: intent.yaw });
        markObs("gamepad", true);
      } else if (!active && ownerRef.current === "gamepad") {
        ownerRef.current = null;
        real.setAnalog({ pitch: 0, roll: 0, lift: 0, yaw: 0 });
        markObs(null, false);
      }
      // boutons discrets (hover/land/rtl/vitesse) sur front montant — méthodes réelles existantes.
      if (snap?.buttons && profile) {
        const prev = prevBtnRef.current;
        const guard = btnGuardRef.current;
        // R2 : « visible » seul ne suffit pas, la fenêtre OS doit réellement avoir le focus.
        const focused = document.visibilityState !== "hidden"
          && (typeof document.hasFocus !== "function" || document.hasFocus());
        snap.buttons.forEach((b, idx) => {
          const a = profile.button_mapping?.[String(idx)];
          if (!focused) guard.armed.delete(idx);
          else if (!b.pressed) guard.armed.add(idx);  // relâchement observé, cockpit réellement actif
          const allowed = focused && guard.armed.has(idx);
          if (allowed && a && DISCRETE_ACTIONS.includes(a) && b.pressed && !prev[idx]?.pressed) {
            if (a === "hover") real.hover();
            else if (a === "land_sim") real.landReal();
            else if (a === "rtl_sim") real.rtlReal();
            else if (a === "speed_up") real.speedUp();
            else if (a === "speed_down") real.speedDown();
          }
        });
        prevBtnRef.current = snap.buttons.map((b) => ({ pressed: b.pressed }));
      }
    }, 50);
    return () => clearInterval(id);
  }, [enabled, real, padRef, profile, markObs]);

  // ---- Touch : joystick directionnel (press/release) + boutons hover/land/rtl. ----
  const touchHold = useCallback((dir, on) => {
    if (!real) return;
    if (on) {
      if (arbitrate(ownerRef.current, "touch", true) !== "touch") return; // owner occupé
      ownerRef.current = "touch";
      touchHeldRef.current.add(dir);
      real.press(dir);
      markObs("touch", true);
    } else {
      if (!touchHeldRef.current.has(dir)) return;
      touchHeldRef.current.delete(dir);
      real.release(dir);
      if (touchHeldRef.current.size === 0) { ownerRef.current = arbitrate(ownerRef.current, "touch", false); markObs(null, false); }
    }
  }, [real, markObs]);

  const touchAction = useCallback((a) => {
    if (!real) return;
    if (a === "hover") real.hover();
    else if (a === "land_sim") real.landReal();
    else if (a === "rtl_sim") real.rtlReal();
  }, [real]);

  return { input: obs, touchHold, touchAction };
}
